import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Pagination } from '../../../shared/components/Pagination';
import { Skeleton } from '../../../shared/components/Skeleton';
import { TextInput } from '../../../shared/components/TextInput';
import { FEATURES } from '../../../shared/lib/permissions';
import { useRequisicoes } from '../hooks';
import type { JobRequisitionStatus } from '../api';
import { DevolucoesBadge, RequisicaoStatusBadge } from '../components/RecrutamentoBadges';
import { JOB_TYPE_LABELS, formatCurrencyBRL, formatDateBR, formatClienteLabel } from '../components/format';

const PAGE_SIZE = 25;

const STATUS_OPTIONS: Array<{ value: JobRequisitionStatus | ''; label: string }> = [
  { value: '', label: 'Todos os status' },
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'em_aprovacao', label: 'Em aprovação' },
  { value: 'devolvida', label: 'Devolvida' },
  { value: 'aprovada', label: 'Aprovada' },
  { value: 'reprovada', label: 'Reprovada' },
  { value: 'cancelada', label: 'Cancelada' },
];

/** Lista de requisições de vaga, com busca por código, cargo ou área. */
export default function RequisicoesPage(): React.ReactElement {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<JobRequisitionStatus | ''>('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [offset, setOffset] = useState(0);

  // Busca só viaja depois que o usuário para de digitar.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, isError } = useRequisicoes({
    search: search || undefined,
    status: status || undefined,
    page: Math.floor(offset / limit) + 1,
    limit,
  });

  const requisicoes = data?.data ?? [];

  return (
    <PageContainer
      title="Requisições de vaga"
      subtitle="Abertura e acompanhamento das solicitações de contratação"
      action={
        <Can feature={FEATURES.recrutamentoRequisicoes} action="create">
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/recrutamento/requisicoes/nova')}>
            Nova requisição
          </Button>
        </Can>
      }
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <TextInput
            label="Buscar por código, cargo ou área"
            icon={<Search className="h-4 w-4" />}
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setOffset(0);
            }}
          />
        </div>
        <div className="w-56">
          <AutocompleteSelect
            label="Status"
            options={STATUS_OPTIONS}
            value={status}
            onChange={(value) => {
              setStatus(value as JobRequisitionStatus | '');
              setOffset(0);
            }}
          />
        </div>
      </div>

      {isError && <Alert variant="error">Não foi possível carregar as requisições.</Alert>}

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : requisicoes.length === 0 ? (
        <EmptyState
          title="Nenhuma requisição encontrada"
          description="Abra uma requisição para iniciar um processo de contratação."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Código</th>
                <th className="px-3 py-2 text-left">Cargo</th>
                <th className="px-3 py-2 text-left">Cliente</th>
                <th className="px-3 py-2 text-left">Área</th>
                <th className="px-3 py-2 text-right">Vagas</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-right">Salário previsto</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Criada em</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {requisicoes.map((requisicao) => (
                <tr
                  key={requisicao.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/recrutamento/requisicoes/${requisicao.id}`)}
                >
                  <td className="px-3 py-2 font-medium text-gray-900">{requisicao.code}</td>
                  <td className="px-3 py-2">{requisicao.positionTitle}</td>
                  <td className="px-3 py-2">{formatClienteLabel(requisicao.client)}</td>
                  <td className="px-3 py-2">{requisicao.requestingArea}</td>
                  <td className="px-3 py-2 text-right">{requisicao.vacanciesCount}</td>
                  <td className="px-3 py-2">{JOB_TYPE_LABELS[requisicao.jobType]}</td>
                  <td className="px-3 py-2 text-right">
                    {formatCurrencyBRL(requisicao.expectedSalary)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-1">
                      <RequisicaoStatusBadge status={requisicao.status} />
                      <DevolucoesBadge count={requisicao.returnCount} />
                    </div>
                  </td>
                  <td className="px-3 py-2">{formatDateBR(requisicao.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <Pagination
          offset={offset}
          limit={limit}
          count={data.total}
          itemLabel="requisição"
          itemLabelPlural="requisições"
          onChange={(next) => {
            setOffset(next.offset);
            setLimit(next.limit);
          }}
        />
      )}
    </PageContainer>
  );
}
